import { useState } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { clsx } from 'clsx';
import { FeedbackModal } from '../features/FeedbackModal';
import { useI18n } from '../../i18n/I18nContext';

interface QuickNavItem {
  path: string;
  labelKey: string;
  icon: JSX.Element;
}

const quickNavItems: QuickNavItem[] = [
  {
    path: '/dashboard',
    labelKey: 'nav.dashboard',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path strokeLinecap="round" strokeLinejoin="round" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
      </svg>
    ),
  },
  {
    path: '/commissions',
    labelKey: 'nav.commissions',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
  },
  {
    path: '/book-of-business',
    labelKey: 'nav.book',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
      </svg>
    ),
  },
  {
    path: '/points',
    labelKey: 'nav.points',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <polyline points="23 6 13.5 15.5 8.5 10.5 1 18" />
        <polyline points="17 6 23 6 23 12" />
      </svg>
    ),
  },
  {
    path: '/hierarchy',
    labelKey: 'nav.hierarchy',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path strokeLinecap="round" strokeLinejoin="round" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
      </svg>
    ),
  },
];

interface MvpQuickNavProps {
  onMenuClick?: () => void;
}

export function MvpQuickNav({ onMenuClick }: MvpQuickNavProps) {
  const location = useLocation();
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const { t } = useI18n();

  return (
    <>
    <FeedbackModal isOpen={feedbackOpen} onClose={() => setFeedbackOpen(false)} />
    <nav className="quick-nav" aria-label="Quick navigation">
      <div className="quick-nav-items">
        {/* Menu button — first item */}
        <button
          type="button"
          onClick={onMenuClick}
          className="quick-nav-item quick-nav-menu"
          aria-label="Open menu"
        >
          <span className="quick-nav-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="3" y1="12" x2="21" y2="12" />
              <line x1="3" y1="6" x2="21" y2="6" />
              <line x1="3" y1="18" x2="21" y2="18" />
            </svg>
          </span>
          <span className="quick-nav-label">{t('nav.menu')}</span>
        </button>

        {quickNavItems.map((item) => {
          const isActive = item.path === '/'
            ? location.pathname === '/'
            : location.pathname.startsWith(item.path);

          return (
            <Link
              key={item.path}
              to={item.path}
              className={clsx('quick-nav-item', isActive && 'active')}
              aria-current={isActive ? 'page' : undefined}
            >
              <span className="quick-nav-icon">{item.icon}</span>
              <span className="quick-nav-label">{t(item.labelKey)}</span>
            </Link>
          );
        })}

        {/* Feedback button */}
        <button
          type="button"
          onClick={() => setFeedbackOpen(true)}
          className="quick-nav-item"
          aria-label="Send feedback"
        >
          <span className="quick-nav-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
            </svg>
          </span>
          <span className="quick-nav-label">{t('nav.support')}</span>
        </button>
      </div>
    </nav>
    </>
  );
}
